import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { catchError, tap, map, debounceTime, finalize } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { Notificacion, TipoNotificacion } from '@app/models/notificacion.model';
import { GeneralService } from '@app/services/general.service';
import { PaginatedResponse } from '@app/models/backend/notification/paginated-response.model';
import {
  WebSocketService,
  WebSocketMessage,
} from '@app/services/websocket/WebSocketService';

// ▲ cerca del top del archivo

export interface PageBackend<T> {
  content?: T[];
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
}

// si ReaderDTO no está exportado aún, deja así:
export type ReaderDTO = {
  userId: number | null;
  username?: string | null;
  email?: string | null;
  nombre?: string | null;
  apellido?: string | null;
  sedeNombre?: string | null;
  sedeId?: number | null;
  readAt?: string | null;
};

/** Respuesta envoltorio del backend { rpta, msg, data } */
interface ApiResponse<T> {
  rpta?: number;
  msg?: string;
  data: T;
}

/** Notificación tal como llega del backend para el LISTADO (con readers opcional) */
interface BackendNotification {
  id: number;
  recipientId: number | null;
  recipientName?: string | null;
  senderId?: number | null;
  senderName?: string | null;
  title: string;
  message: string;
  type: string | null; // INFO | ALERT | SYSTEM | BROADCAST ...
  category?: string | null; // GENERAL, etc.
  read: boolean;
  createdAt: string; // puede venir ISO o "DD/MM/YYYY HH:mm:ss"
  avatar?: string | null;

  // extras del listado:
  data?: any;
  readCount?: number;
  readers?: Array<any>; // preview (array), puede no venir
  sedeId?: number | null;
  sedeName?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class NotificacionesHttpService implements OnDestroy {
  private baseUrl = `${environment.url}api/notifications`;

  // Estado reactivo
  private notificacionesSubject = new BehaviorSubject<Notificacion[]>([]);
  private contadorNoLeidasSubject = new BehaviorSubject<number>(0);
  private cargandoSubject = new BehaviorSubject<boolean>(false);

  // Observables públicos
  public notificaciones$ = this.notificacionesSubject.asObservable();
  public contadorNoLeidas$ = this.contadorNoLeidasSubject.asObservable();
  public cargando$ = this.cargandoSubject.asObservable().pipe(debounceTime(50));

  // Suscripciones WebSocket
  private wsSubscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private authService: GeneralService,
    private webSocketService: WebSocketService
  ) {
    this.initializeWebSocket();
  }

  ngOnDestroy(): void {
    this.wsSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initializeWebSocket(): void {
    // El WebSocketService ya se conecta automáticamente en su constructor
    // Solo necesitamos suscribirnos a los mensajes relevantes

    // Suscribirse a notificaciones nuevas
    const newNotificationSub = this.webSocketService
      .getMessagesByType('NOTIFICATIONS')
      .subscribe((payload: any) => {
        if (payload && payload.notification) {
          this.handleNewNotification(payload.notification);
        }
      });

    // Suscribirse a notificaciones broadcast
    const broadcastSub = this.webSocketService
      .getMessagesByType('BROADCAST_NOTIFICATION')
      .subscribe((payload: any) => {
        if (payload && payload.notification) {
          this.handleNewNotification(payload.notification);
        }
      });

    // Suscribirse a actualizaciones de contador
    const countUpdateSub = this.webSocketService
      .getMessagesByType('COUNT')
      .subscribe((payload: any) => {
        if (payload && payload.count !== undefined) {
          this.contadorNoLeidasSubject.next(payload.count);
        }
      });

    this.wsSubscriptions.push(newNotificationSub, broadcastSub, countUpdateSub);
  }

  private handleNewNotification(notification: any): void {
    // Convertir notificación del backend al formato del frontend
    const notificacionFrontend = this.mapBackendToFrontend(notification);

    // Agregar a la lista actual
    const notificacionesActuales = this.notificacionesSubject.value;
    const notificacionesActualizadas = [
      notificacionFrontend,
      ...notificacionesActuales,
    ];
    this.notificacionesSubject.next(notificacionesActualizadas);

    // Actualizar contador si es necesario
    if (!notificacionFrontend.leida) {
      const contadorActual = this.contadorNoLeidasSubject.value;
      this.contadorNoLeidasSubject.next(contadorActual + 1);
    }
  }

  // ============================================================
  // Listado principal (paginado)
  // ============================================================
  // Paginated list — respeta los endpoints del backend
  obtenerNotificaciones(
    page: number = 0,
    size: number = 10,
    filtro: string = 'todas'
  ): Observable<PaginatedResponse<Notificacion>> {
    const userId = this.authService.getUserId();
    if (!userId) {
      return of(this.getEmptyPaginatedResponse());
    }

    this.cargandoSubject.next(true);

    const f = (filtro || 'todas').toLowerCase();
    let url = '';
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    switch (f) {
      case 'no-leidas':
        url = `${this.baseUrl}/list/unread/${userId}`;
        break;
      case 'leidas':
        url = `${this.baseUrl}/list/read/${userId}`;
        break;
      case 'enviadas':
        url = `${this.baseUrl}/sent/user/${userId}`;
        break;
      case 'recibidas':
        url = `${this.baseUrl}/received/user/${userId}`;
        break;
      // filtros por tipo/categoría (info, exito, advertencia, error, etc.)
      case 'info':
      case 'éxito':
      case 'exito':
      case 'advertencia':
      case 'error':
        url = `${this.baseUrl}/search`;
        params = params
          .set('userId', String(userId))
          .set('filter', f.toUpperCase()); // el backend espera el filtro en mayúsculas
        break;
      // todas
      default:
        url = `${this.baseUrl}/user/${userId}`;
        break;
    }

    return this.http
      .get<
        | ApiResponse<PageBackend<BackendNotification>>
        | PageBackend<BackendNotification>
      >(url, { params })
      .pipe(
        map((resp) => this.normalizeAndMapPage(resp)),
        tap((res) => {
          this.notificacionesSubject.next(res.content);
        }),
        finalize(() => this.cargandoSubject.next(false)),
        catchError((err) => {
          console.error('❌ Error al obtener notificaciones:', err);
          this.cargandoSubject.next(false);
          return of(this.getEmptyPaginatedResponse());
        })
      );
  }

  // ============================================================
  // Contador de no leídas
  // ============================================================
  /**
   * Devuelve el contador de no leídas como number (el componente lo llama aquí).
   */
  public obtenerContadorNoLeidas(): Observable<number> {
    const userId = this.authService.getUserId?.();
    if (!userId) {
      this.contadorNoLeidasSubject.next(0);
      return of(0);
    }
    const url = `${this.baseUrl}/unread-count/${userId}`;

    return this.http.get<ApiResponse<number> | { data: number }>(url).pipe(
      map((resp: any) => (typeof resp?.data === 'number' ? resp.data : 0)),
      tap((n) => this.contadorNoLeidasSubject.next(n)),
      catchError((err) => {
        console.error('❌ Error al obtener contador no leídas:', err);
        this.contadorNoLeidasSubject.next(0);
        return of(0);
      })
    );
  }

  /** Permite actualizar el contador localmente (usado por WS) */
  public actualizarContador(n: number): void {
    const val = Number(n) || 0;
    this.contadorNoLeidasSubject.next(val);
  }

  /** Alias por compatibilidad si el componente lo llamaba con otro nombre */
  public actualizarContadorLocal(n: number): void {
    this.actualizarContador(n);
  }

  /** Forzar refresh típico (re-usa el contador); puedes ampliar si quieres recargar listado, etc. */
  public refrescarNotificaciones(): void {
    this.obtenerContadorNoLeidas().subscribe();
  }

  // ============================================================
  // Acciones
  // ============================================================
  public marcarComoLeida(notificationId: number): Observable<boolean> {
    const userId = this.authService.getUserId?.();
    if (!userId) return of(false);

    const url = `${this.baseUrl}/mark-as-read/${notificationId}/${userId}`;
    return this.http
      .put<{ data: boolean } | { rpta?: number; data: boolean }>(url, null)
      .pipe(
        map((response: any) => response?.data === true),
        tap((success) => {
          if (success) {
            // actualizar lista local
            const list = this.notificacionesSubject.value.map((n) =>
              n.id === notificationId ? { ...n, leida: true } : n
            );
            this.notificacionesSubject.next(list);
          }
        }),
        catchError((error) => {
          console.error('❌ Error al marcar como leída:', error);
          return of(false);
        })
      );
  }

  /** Acepta userId opcional por compatibilidad con tu componente */
  public marcarTodasComoLeidas(userId?: number): Observable<boolean> {
    const uid = userId ?? this.authService.getUserId?.();
    if (!uid) return of(false);

    const url = `${this.baseUrl}/mark-all-as-read/${uid}`;
    // tu backend: PATCH o PUT; ajusta si fuera PUT
    return this.http
      .patch<{ data: boolean } | { rpta?: number; data: boolean }>(url, {})
      .pipe(
        map((res: any) => res?.data === true),
        catchError((err) => {
          console.error('❌ Error al marcar todas como leídas:', err);
          return of(false);
        })
      );
  }

  public eliminarNotificacion(notificationId: number): Observable<boolean> {
    return this.http.delete<any>(`${this.baseUrl}/${notificationId}`).pipe(
      map((response) => {
        // El backend devuelve 204 No Content cuando es exitoso
        // Si llegamos aquí, significa que fue exitoso
        return true;
      }),
      tap((ok) => {
        if (ok) {
          const filtered = this.notificacionesSubject.value.filter(
            (n) => n.id !== notificationId
          );
          this.notificacionesSubject.next(filtered);
        }
      }),
      catchError((error) => {
        console.error('❌ Error al eliminar notificación:', error);
        return of(false);
      })
    );
  }

  public eliminarNotificaciones(
    notificationIds: number[]
  ): Observable<boolean> {
    return (
      this.http
        // 👇 SIN /bulk  y el body es el array plano, no { ids: ... }
        .delete<any>(`${this.baseUrl}`, {
          body: notificationIds,
          headers: { 'Content-Type': 'application/json' },
        })
        .pipe(
          // tu backend devuelve GenericResponse<Integer>
          // rpta === 1 y data = cantidad eliminada
          map((resp) => resp && resp.rpta === 1 && Number(resp.data) > 0),
          tap((ok) => {
            if (ok) {
              const filtered = this.notificacionesSubject.value.filter(
                (n) => !notificationIds.includes(n.id)
              );
              this.notificacionesSubject.next(filtered);
            }
          }),
          catchError((error) => {
            console.error('❌ Error al eliminar notificaciones:', error);
            return of(false);
          })
        )
    );
  }

  // ======= Envíos (si los usas en esta pantalla) =======
  public enviarNotificacion(payload: any): Observable<any> {
    return this.http.post<any>(this.baseUrl, payload).pipe(
      catchError((error) => {
        console.error('❌ Error al enviar notificación:', error);
        throw error;
      })
    );
  }

  public enviarNotificacionPorRol(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/enviar-por-rol`, payload).pipe(
      catchError((error) => {
        console.error('❌ Error al enviar notificación por rol:', error);
        throw error;
      })
    );
  }

  public enviarNotificacionPorSede(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/enviar-por-sede`, payload).pipe(
      catchError((error) => {
        console.error('❌ Error al enviar notificación por sede:', error);
        throw error;
      })
    );
  }

  public enviarNotificacionPorSedeYRol(payload: any): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/enviar-por-sede-rol`, payload)
      .pipe(
        catchError((error) => {
          console.error(
            '❌ Error al enviar notificación por sede y rol:',
            error
          );
          throw error;
        })
      );
  }
  // ============================================================
  // Helpers de mapeo
  // ============================================================
  private normalizeAndMapPage(
    resp:
      | ApiResponse<PageBackend<BackendNotification>>
      | PageBackend<BackendNotification>
  ): PaginatedResponse<Notificacion> {
    const pageBackend = (resp as any)?.data ?? resp;

    // ⚠️ Aseguramos el genérico a BackendNotification para que no quede como unknown[]
    const pageFilled = this.fillPageDefaults<BackendNotification>(
      pageBackend as Partial<PageBackend<BackendNotification>>
    );

    // Narrow seguro del contenido
    const raw = Array.isArray(pageFilled.content) ? pageFilled.content : [];
    const backendList: BackendNotification[] = raw as BackendNotification[];

    const content: Notificacion[] = backendList.map((n) =>
      this.mapBackendToFrontend(n)
    );

    const out: PaginatedResponse<Notificacion> = {
      content,
      totalPages: Number(pageFilled.totalPages ?? 0),
      totalElements: Number(pageFilled.totalElements ?? content.length),
      number: Number(pageFilled.number ?? 0),
      size: Number(pageFilled.size ?? content.length),
      first: Boolean(pageFilled.first ?? Number(pageFilled.number ?? 0) === 0),
      last: Boolean(
        pageFilled.last ??
        Number(pageFilled.number ?? 0) + 1 >=
        Number(pageFilled.totalPages ?? 0)
      ),
      numberOfElements: Number(pageFilled.numberOfElements ?? content.length),
      empty: content.length === 0,
    };
    return out;
  }

  private fillPageDefaults<T>(page?: Partial<PageBackend<T>>): PageBackend<T> {
    const content = Array.isArray(page?.content) ? (page!.content as T[]) : [];
    const numberOfElements =
      typeof page?.numberOfElements === 'number'
        ? page!.numberOfElements
        : content.length;
    const size = typeof page?.size === 'number' ? page!.size : content.length;
    const totalElements =
      typeof page?.totalElements === 'number'
        ? page!.totalElements
        : numberOfElements;
    const totalPages =
      typeof page?.totalPages === 'number'
        ? page!.totalPages
        : size > 0
          ? Math.ceil(totalElements / size)
          : 0;
    const number = typeof page?.number === 'number' ? page!.number : 0;

    return {
      content,
      totalElements,
      totalPages,
      number,
      size,
      first: typeof page?.first === 'boolean' ? page!.first! : number === 0,
      last:
        typeof page?.last === 'boolean'
          ? page!.last!
          : number + 1 >= totalPages,
      numberOfElements,
      empty: content.length === 0,
    };
  }

  /** Convierte "DD/MM/YYYY HH:mm:ss" a ISO; si ya es ISO/parseable, devuelve ISO */
  private parseCustomDateString(dateStr: string | null | undefined): string {
    if (!dateStr) return new Date(NaN).toISOString();

    // Si parece ISO (contiene '-' o 'T'), no tocar
    if (dateStr.includes('-') || dateStr.includes('T')) {
      const d = new Date(dateStr);
      return isNaN(+d) ? new Date(NaN).toISOString() : d.toISOString();
    }

    // Formato DD/MM/YYYY HH:mm:ss
    if (dateStr.includes('/')) {
      const [fecha, hora = '00:00:00'] = dateStr.split(' ');
      const [dS, mS, yS] = fecha.split('/');
      const [hS, minS, sS] = hora.split(':');

      const d = parseInt(dS, 10);
      const m = parseInt(mS, 10) - 1;
      const y = parseInt(yS, 10);
      const hh = parseInt(hS || '0', 10);
      const mm = parseInt(minS || '0', 10);
      const ss = parseInt(sS || '0', 10);

      const dt = new Date(y, m, d, hh, mm, ss);
      return isNaN(+dt) ? new Date(NaN).toISOString() : dt.toISOString();
    }

    // Último intento
    const dt = new Date(dateStr);
    return isNaN(+dt) ? new Date(NaN).toISOString() : dt.toISOString();
  }

  private mapBackendToFrontend(n: BackendNotification): Notificacion {
    const leida = !!n.read;

    return {
      id: n.id,
      usuarioId: n.recipientId ?? 0,
      titulo: n.title || 'Sin título',
      mensaje: n.message || 'Sin mensaje',
      tipo: this.mapTipoNotificacion((n.type || '').toUpperCase()),
      leida: leida,
      fechaCreacion: this.parseCustomDateString(n.createdAt),
      datos: n?.data,
      readCount: typeof n.readCount === 'number' ? n.readCount : 0,
      readers: Array.isArray(n.readers) ? n.readers : [],
      senderName: n.senderName ?? undefined,
      senderId: n.senderId ?? undefined,
      recipientId: n.recipientId ?? undefined,
      recipientName: n.recipientName ?? undefined,
      backendType: n.type ?? undefined, // Preservar el tipo original del backend
      sedeId: n.sedeId ?? undefined,
      sedeName: n.sedeName ?? undefined,
    };
  }

  private mapTipoNotificacion(backendType: string): TipoNotificacion {
    switch (backendType) {
      case 'INFO':
      case 'USER_MESSAGE':
      case 'BROADCAST': // lo muestras como INFO en listado
        return TipoNotificacion.INFO;
      case 'SUCCESS':
        return TipoNotificacion.EXITO;
      case 'ALERT':
      case 'WARNING':
        return TipoNotificacion.ADVERTENCIA;
      case 'ERROR':
        return TipoNotificacion.ERROR;
      case 'SYSTEM':
        return TipoNotificacion.SISTEMA;
      default:
        return TipoNotificacion.INFO;
    }
  }

  /** Respuesta paginada vacía (segura con todos los campos) */
  private getEmptyPaginatedResponse(): PaginatedResponse<Notificacion> {
    return {
      content: [],
      totalPages: 0,
      totalElements: 0,
      last: true,
      first: true,
      size: 0,
      number: 0,
      numberOfElements: 0,
      empty: true,
    };
  }

  obtenerLectoresPaginados(
    notificationId: number,
    page: number,
    size: number
  ): Observable<PageBackend<ReaderDTO>> {
    const url = `${this.baseUrl}/${notificationId}/readers`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    return this.http
      .get<ApiResponse<PageBackend<ReaderDTO>> | PageBackend<ReaderDTO>>(url, {
        params,
      })
      .pipe(
        map((resp) => {
          const data = (resp as any)?.data ?? resp;
          const pageObj: PageBackend<ReaderDTO> = {
            content: Array.isArray(data?.content) ? data.content : [],
            totalElements: Number(data?.totalElements ?? 0),
            totalPages: Number(data?.totalPages ?? 0),
            number: Number(data?.number ?? 0),
            size: Number(data?.size ?? 0),
            first: Boolean(data?.first ?? Number(data?.number ?? 0) === 0),
            last: Boolean(
              data?.last ??
              Number(data?.number ?? 0) + 1 >= Number(data?.totalPages ?? 0)
            ),
            numberOfElements: Number(
              data?.numberOfElements ??
              (Array.isArray(data?.content) ? data.content.length : 0)
            ),
            empty: Boolean(
              data?.empty ??
              (Array.isArray(data?.content)
                ? data.content.length === 0
                : true)
            ),
          };
          return pageObj;
        }),
        catchError(() =>
          of({
            content: [],
            totalElements: 0,
            totalPages: 0,
            number: 0,
            size: size,
            first: true,
            last: true,
            numberOfElements: 0,
            empty: true,
          })
        )
      );
  }
}